import { useState } from "react";
import { Camera, ImagePlus, Images } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PhotoPageCard } from "./PhotoPageCard";
import { PropertySelect } from "./PropertySelect";
import { api, errorMessage } from "@/lib/api";

const MAX = 20;
const ZERO = { left: 0, right: 0, top: 0, bottom: 0 };
const OK = /\.(jpe?g|png|heic|heif)$/i;
const defaultName = () => `Photo scan ${new Date().toISOString().slice(0, 10)}`;

export const PhotoPdfDialog = ({ workspace, properties, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [name, setName] = useState(defaultName());
  const [pid, setPid] = useState(properties[0]?.id || null);
  const [busy, setBusy] = useState(false);

  const add = async (fileList) => {
    let count = pages.length;
    for (const f of Array.from(fileList || [])) {
      if (count >= MAX) { toast.error(`A PDF can have up to ${MAX} pages.`); break; }
      if (!OK.test(f.name) && !/^image\/(jpeg|png|heic|heif)$/.test(f.type)) { toast.error(`${f.name}: only JPG, PNG and HEIC photos can be added.`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is larger than 10 MB.`); continue; }
      count += 1; setUploading((u) => u + 1);
      const fd = new FormData();
      fd.append("file", f, OK.test(f.name) ? f.name : `${f.name || "photo"}.jpg`);
      try { const { data } = await api.post(`/workspaces/${workspace.id}/photo-pages`, fd); setPages((ps) => [...ps, { ...data.page, rotate: 0, crop: ZERO, scan: false }]); }
      catch (e) { count -= 1; toast.error(errorMessage(e)); } finally { setUploading((u) => u - 1); }
    }
  };
  const update = (i, patch) => setPages((ps) => ps.map((p, n) => (n === i ? { ...p, ...patch } : p)));
  const move = (i, d) => setPages((ps) => { const a = [...ps]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; });
  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspace.id}/photo-pdf`, { name, property_id: pid, pages: pages.map(({ id, rotate, crop, scan }) => ({ id, rotate, crop, scan })) });
      toast.success("PDF saved to your documents."); setPages([]); setName(defaultName()); setOpen(false); onCreated();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={workspace.is_sample} data-testid="photo-pdf-open-button"><Images className="mr-1.5 h-4 w-4" /> Photos to PDF</Button>
      </DialogTrigger>
      <DialogContent aria-describedby={undefined} data-testid="photo-pdf-dialog" className="max-h-[92vh] max-w-3xl overflow-y-auto bg-white">
        <DialogHeader><DialogTitle className="font-display text-navy">Turn photos of a form into a PDF</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">One photo per page. JPG, PNG or HEIC, up to 10 MB each and {MAX} pages. Photos stay private to this workspace. Use Read with AI later to recognize the text.</p>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy-2">
            <Camera className="h-4 w-4" /> Take photo
            <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="photo-pdf-camera-input" />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium text-navy hover:bg-navy/5">
            <ImagePlus className="h-4 w-4" /> Choose photos
            <input type="file" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" multiple className="sr-only" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="photo-pdf-file-input" />
          </label>
        </div>
        {uploading > 0 && <p className="text-sm text-slate-600" data-testid="photo-pdf-uploading">Adding {uploading} photo(s)...</p>}
        {pages.length > 0 && <ul className="space-y-3" data-testid="photo-pdf-pages">{pages.map((p, i) => (
          <PhotoPageCard key={p.id} page={p} index={i} total={pages.length} onChange={(patch) => update(i, patch)} onMove={(d) => move(i, d)} onRemove={() => setPages((ps) => ps.filter((_, n) => n !== i))} />
        ))}</ul>}
        {pages.length > 0 && (
          <div className="grid gap-3 border-t pt-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div className="space-y-1"><p className="text-sm font-medium text-navy">PDF name</p><Input value={name} maxLength={150} onChange={(e) => setName(e.target.value)} data-testid="photo-pdf-name-input" className="h-11" /></div>
            <div className="space-y-1"><p className="text-sm font-medium text-navy">Property</p><PropertySelect properties={properties} value={pid} onChange={setPid} testId="photo-pdf-property-select" /></div>
            <Button onClick={create} disabled={busy || uploading > 0 || !name.trim()} data-testid="photo-pdf-create-button" className="h-11 bg-navy hover:bg-navy-2">{busy ? "Creating PDF..." : `Create ${pages.length}-page PDF`}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
